import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { blogs } from "../data/blogs.js";
import Navbar from "../components/Navbar.js";
import Footer from "../components/Footer.js";
import parse, { domToReact } from "html-react-parser";

// FadeInSection component for fade-in on scroll
const FadeInSection = ({ children }) => {
  const ref = useRef();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(ref.current);
        }
      },
      { threshold: 0.8 }
    );

    if (ref.current) observer.observe(ref.current);

    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-opacity duration-1000 ease-out ${
        visible ? "opacity-100" : "opacity-30"
      }`}
    >
      {children}
    </div>
  );
};

const BlogDetail = () => {
  const { id } = useParams();
  const blog = blogs.find((b) => b.id.toString() === id);
  const navigate = useNavigate();

  if (!blog) {
    return <h1 className="text-white text-center mt-10">Blog not found</h1>;
  }

  // Parse blog.content HTML string, wrap block elements in FadeInSection
  const parsedContent = parse(blog.content, {
    replace: (domNode) => {
      if (!domNode || !domNode.name) return;

      // Define which tags to wrap with fade effect
      const blockTags = [
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "p",
        "ul",
        "ol",
        "li",
        "div",
        "section",
        "article",
        "blockquote",
      ];

      if (blockTags.includes(domNode.name)) {
        return (
          <FadeInSection key={domNode.attribs?.key || undefined}>
            {React.createElement(
              domNode.name,
              { className: domNode.attribs?.class || undefined },
              domToReact(domNode.children)
            )}
          </FadeInSection>
        );
      }
    },
  });

  return (
    <>
      <Navbar />
      <div className="bg-black text-white relative overflow-hidden rounded-3xl md:rounded-[3rem] px-16 py-10">
        {/* Top Light Effect Gradient */}
        <div className="absolute top-[-200px] left-1/2 transform -translate-x-1/2 w-[1000px] h-[700px] bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full blur-3xl opacity-20 z-0" />

        <div className="relative z-10 max-w-3xl mx-auto px-6 py-20 text-center">
          {/* Breadcrumb */}
          <div className="text-sm uppercase tracking-widest text-gray-400 mb-4">
            Bloglist /
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight text-white mb-6">
            {blog.title}
          </h1>

          <div>
            <button
              className="flex items-center text-purple-300 hover:text-white transition mb-8"
              onClick={() => navigate("/blogs")}
            >
              Back
            </button>
          </div>

          {/* Author Info */}
          <div className="flex justify-center items-center space-x-3 mb-8">
            <div className="text-left">
              <div className="text-sm text-gray-400">{blog.date}</div>
            </div>
          </div>

          {/* Description always white */}
          <p className="mb-8 text-white max-w-2xl mx-auto text-left">
            {blog.description}
          </p>

          {/* Parsed content with fade-in */}
          <div className="max-w-2xl mx-auto text-left">{parsedContent}</div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BlogDetail;
